import { useCallback } from 'react'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useUpdateBrandPlan, Variables as UpdateBrandPlanVariables } from 'hooks/queries/useUpdateBrandPlan'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BlogPlatformIntegration, EmailPlatformIntegration } from 'types'

/**
 * Hook that provides a function to update the brand plan's selected platform (blog or email).
 * @returns A function that updates the selected platform based on the tactic and optional integration.
 */
export const useUpdateBrandPlanSelectedPlatform = () => {
  const { data: brandPlan } = useCurrentBrandPlan()
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlan?.id || 0 })
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const updateBrandPlanSelectedPlatform = useCallback(
    async (tactic: 'seo' | 'email', integration?: ExternalIntegration) => {
      if (!brandPlan) {
        return
      }

      const updatePayload: UpdateBrandPlanVariables = {}

      if (tactic === 'seo') {
        const platformValue: BlogPlatformIntegration = integration
          ? (integration.provider as BlogPlatformIntegration)
          : 'generic'
        updatePayload.selected_blog_platform = platformValue
      } else if (tactic === 'email') {
        const platformValue: EmailPlatformIntegration = integration
          ? (integration.provider as EmailPlatformIntegration)
          : 'generic'
        updatePayload.selected_email_platform = platformValue
      }

      await updateBrandPlan(updatePayload)
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandPlans', 'current'])
    },
    [brandPlan, updateBrandPlan, queryClient, workspaceId],
  )

  return updateBrandPlanSelectedPlatform
}
